/* eslint-disable react/no-array-index-key */
import { Grid2, Stack } from '@mui/material';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { PropsOf } from '@emotion/react';
import { CSSProperties } from 'react';
import TextInput from '../../form/base/TextInput';
import SingleSelect from '../../form/base/SingleSelect';
import MultiSelect from '../../form/base/MultiSelect';
import { isNil } from '../../../utils/global/helper';
import BirthdayInput from '../../form/base/BirthdayInput';
import ColoredAlert, { ColoredAlertProps } from '../ColoredAlert';
import TextareaInput from '../../form/base/TextareaInput';
import SmallModal, { Footer } from '../SmallModal';
import CheckboxInput from '../../form/base/CheckboxInput';
import { FormikForm } from '../../../interfaces';
import FileInput from '../../form/base/FileInput';
import CategorySingleSelect from '../../form/base/CategorySingleSelect';
import MultiCheckboxInput from '../../form/base/MultiCheckboxInput';
import RadioSelect from '../../form/base/RadioSelect';
import MultiSwitchInput from '../../form/base/MultiSwitchInput';

type Input = {
  isVisible?: boolean | ((values: FormikValues) => boolean);
} & (
  | ({
      inputType: 'text' | 'number';
    } & PropsOf<typeof TextInput>)
  | ({
      inputType: 'select';
    } & PropsOf<typeof SingleSelect>)
  | ({
      inputType: 'category-select';
    } & PropsOf<typeof CategorySingleSelect>)
  | ({
      inputType: 'textarea';
    } & PropsOf<typeof TextareaInput>)
  | ({
      inputType: 'multiSelect';
    } & PropsOf<typeof MultiSelect>)
  | ({
      inputType: 'birthday';
    } & PropsOf<typeof BirthdayInput>)
  | ({
      inputType: 'checkbox';
    } & PropsOf<typeof CheckboxInput>)
  | ({
      inputType: 'file';
    } & PropsOf<typeof FileInput>)
  | ({
      inputType: 'multi-checkbox';
    } & PropsOf<typeof MultiCheckboxInput>)
  | ({
      inputType: 'radio-select';
    } & PropsOf<typeof RadioSelect>)
  | ({
      inputType: 'multi-switch';
    } & PropsOf<typeof MultiSwitchInput>)
);

export type InputRow<Values extends FormikValues> = (Input & {
  name: keyof Values;
  required?: boolean;
})[];

export type FormData<Values extends FormikValues = any> = FormikForm<Values> & {
  title?: string;
  submitButtonText?: string;
  inputRows:
    | InputRow<Values>[]
    | ((values: Values, helpers: FormikHelpers<Values>) => InputRow<Values>[]);
};

const formStyle: CSSProperties = {
  width: '100%',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

interface Props {
  open: boolean;
  alert?:
    | ColoredAlertProps
    | ((values: FormikValues) => ColoredAlertProps | null);
  onClose: () => void;
  form: FormData | null;
  maxWidth?: CSSProperties['width'];
  allowUntouched?: boolean;
}

function FormSmallModal({
  open,
  onClose,
  form,
  alert,
  maxWidth,
  allowUntouched = false,
}: Props) {
  if (!form) return null;

  const isInputVisible = (
    input: Pick<Input, 'isVisible' | 'label'>,
    values: FormikValues,
  ) => {
    const value =
      isNil(input.isVisible) ||
      (typeof input.isVisible === 'function'
        ? input.isVisible(values)
        : input.isVisible);
    return value;
  };

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      title={form.title}
      width={maxWidth}>
      {form && (
        <Formik
          enableReinitialize
          initialValues={form.initialValues}
          validationSchema={form.validationSchema}
          onSubmit={async (values, helpers) => {
            try {
              helpers.setSubmitting(true);
              await form.onSubmit(values, helpers);
            } catch {
              // Nothing
            } finally {
              helpers.setSubmitting(false);
            }
          }}>
          {helpers => {
            const { isSubmitting, isValid, isValidating, dirty, values } =
              helpers;
            const inputRows =
              typeof form.inputRows === 'function'
                ? form.inputRows(values, helpers)
                : form.inputRows;
            const alertProps = alert
              ? typeof alert === 'function'
                ? alert(values)
                : alert
              : null;
            return (
              <Form style={formStyle}>
                <Stack p={2} width="100%" gap={2} boxSizing="border-box">
                  {alertProps && <ColoredAlert {...alertProps} />}

                  {inputRows.map((inputRow, index) => {
                    const isAnyInputVisible = inputRow.some(input =>
                      isInputVisible(input, values),
                    );
                    if (!isAnyInputVisible) return null;
                    return (
                      <Grid2
                        container
                        columns={{
                          xs: 1,
                          sm: inputRow.length,
                        }}
                        gap={2}
                        flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                        key={index}
                        maxWidth="100%"
                        width="100%"
                        boxSizing="border-box">
                        {inputRow.map(({ inputType, ...input }, inputIndex) => {
                          const isVisible = isInputVisible(input, values);
                          if (!isVisible) return null;

                          if (inputType === 'text' || inputType === 'number') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <TextInput
                                  type={inputType}
                                  {...(input as PropsOf<typeof TextInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'textarea') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <TextareaInput
                                  {...(input as PropsOf<typeof TextareaInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <SingleSelect
                                  {...(input as PropsOf<typeof SingleSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multiSelect') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiSelect
                                  {...(input as PropsOf<typeof MultiSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'birthday') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <BirthdayInput
                                  {...(input as PropsOf<typeof BirthdayInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'checkbox') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <CheckboxInput
                                  {...(input as PropsOf<typeof CheckboxInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'file') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <FileInput
                                  {...(input as PropsOf<typeof FileInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'category-select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <CategorySingleSelect
                                  {...(input as PropsOf<
                                    typeof CategorySingleSelect
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multi-checkbox') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiCheckboxInput
                                  {...(input as PropsOf<
                                    typeof MultiCheckboxInput
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'radio-select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <RadioSelect
                                  {...(input as PropsOf<typeof RadioSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multi-switch') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiSwitchInput
                                  {...(input as PropsOf<
                                    typeof MultiSwitchInput
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          return null;
                        })}
                      </Grid2>
                    );
                  })}
                </Stack>
                <Footer
                  button={{
                    text: form.submitButtonText ?? 'Update',
                    disabled:
                      isSubmitting ||
                      !isValid ||
                      isValidating ||
                      (!allowUntouched && !dirty),
                    loading: isSubmitting,
                    type: 'submit',
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      )}
    </SmallModal>
  );
}

export default FormSmallModal;
